import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiFolder } from 'react-icons/fi';
import { MdLightbulb } from 'react-icons/md';
import { teamActionTypes } from '@store/Team/Team.actionTypes';
import { Input } from '@/components/Input';
import { Button, buttonVariants } from '@/components/Button';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import {
  IconBadge,
  iconBadgeSizes,
  iconBadgeShapes,
} from '@/components/IconBadge';
import { Callout, calloutVariants } from '@/components/Callout';
import { useFormChanges } from '@/hooks/useFormChanges';
import styles from './Projects.module.scss';

export const ProjectFormModal = ({ onClose, project }) => {
  const [projectName, setProjectName] = useState(project?.project_name || '');
  const [errorMessage, setErrorMessage] = useState('');
  const [requesting, setRequesting] = useState(false);

  const { activeCompany } = useSelector((state) => state.auth.profile);
  const userProfile = useSelector((state) => state.auth.profile);

  const dispatch = useDispatch();
  const isEditMode = Boolean(project);

  const { hasChanges } = useFormChanges(
    { projectName: project?.project_name || '' },
    { projectName },
    ['projectName']
  );

  const handleSubmit = async () => {
    if (!projectName) {
      setErrorMessage('Project Title is required');
      return;
    }
    if (requesting) return;

    setRequesting(true);
    const result = await new Promise((resolve) => {
      if (isEditMode) {
        dispatch({
          type: teamActionTypes.UPDATE_PROJECT,
          payload: {
            project: projectName,
            projectId: project.projectId,
            title: projectName,
            companyId: activeCompany.companyId,
            onComplete: resolve,
          },
        });
      } else {
        dispatch({
          type: teamActionTypes.ADD_PROJECT,
          payload: {
            project: projectName,
            companyId: activeCompany.companyId,
            createdBy: userProfile.id,
            onComplete: resolve,
          },
        });
      }
    });
    setRequesting(false);
    if (result?.success) {
      onClose();
    }
  };

  return (
    <div className={styles.projectFormModal}>
      <div className={styles.projectFormModalHeader}>
        <IconBadge
          icon={<FiFolder size={20} color="var(--indigo-600)" />}
          bg="indigo-100"
          size={iconBadgeSizes.lg}
          shape={iconBadgeShapes.circle}
        />
        <div className={styles.projectFormModalHeaderText}>
          <Text
            variant={textVariantsEnum.Text_lg}
            fontWeight={fontWeightEnum.Semibold}
          >
            {isEditMode ? 'Update Project' : 'Create Project'}
          </Text>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Normal}
            color="gray-600"
          >
            {isEditMode
              ? 'Update your project details.'
              : 'Add a new project to get started'}
          </Text>
        </div>
      </div>

      <div className={styles.projectFormModalField}>
        <Input
          label="Project Title *"
          type="text"
          name="projectName"
          value={projectName}
          onChange={(e) => {
            setErrorMessage('');
            setProjectName(e.target.value);
          }}
          placeholder="Enter project title"
          errorMessage={errorMessage}
          leftBlock={<FiFolder size={16} color="var(--gray-500)" />}
          disabled={requesting}
        />
        <Text variant={textVariantsEnum.Text_xs} color="gray-500">
          Choose a clear and descriptive name for your project.
        </Text>
      </div>

      <Callout
        variant={calloutVariants.tip}
        icon={<MdLightbulb size={14} />}
        title="Tips"
      >
        A good project name helps your team stay organized and easily find what
        they're looking for.
      </Callout>

      <div className={styles.projectFormModalFooter}>
        <Button
          text="Cancel"
          onClick={onClose}
          variant={buttonVariants.secondary}
          disabled={requesting}
        />
        <Button
          onClick={handleSubmit}
          text={isEditMode ? 'Save Changes' : 'Create Project'}
          variant={buttonVariants.primary}
          loading={requesting}
          disabled={isEditMode && !hasChanges}
        />
      </div>
    </div>
  );
};
